import {
  Box,
  Button,
  Card,
  CheckIcon,
  ChevronRightIcon,
  Flex,
  PartnerLogo,
  Tag,
  Typography,
} from '@iheartjane/reefer';
import { useMemo } from 'react';

import styles from './specialCallout.module.css';

export interface SpecialCalloutProps {
  /** Display labels of the weights this special applies to, e.g. `['3.5g', '7g']`. */
  applicableWeights?: string[];
  brandImage?: string | null;
  brandName?: string;
  isApplied: boolean;
  isAuthenticated: boolean;
  isGold?: boolean;
  isQualified: boolean;
  onClick?: () => void;
  segmented?: boolean;
  specialTitle: string;
  specialType: string;
  specialTypeLabel?: string;
  variant: 'pdp' | 'bag' | 'checkout';
}

export const SpecialCallout = ({
  applicableWeights,
  brandImage,
  brandName,
  isApplied,
  isAuthenticated,
  isGold,
  isQualified,
  onClick,
  segmented = false,
  specialTitle,
  specialType,
  specialTypeLabel,
  variant,
}: SpecialCalloutProps) => {
  const isBundle = specialType === 'bundle';
  const isProduct = specialType === 'product';
  const isUnlocked = isApplied || isQualified;
  const isBag = variant === 'bag';
  const isCheckout = variant === 'checkout';
  const isPdp = variant === 'pdp';

  // Bundle specials show a "Complete bundle" CTA until unlocked
  // Non-gold specials show a "Complete deal" CTA until applied
  const showCta = isGold ? isBundle && !isUnlocked : !isApplied && !isProduct;
  const showButtons = onClick && showCta;
  const showSignIn = segmented && !isAuthenticated;
  const showCheck = isApplied && (isAuthenticated || !segmented);
  const showBrandName = isBag && brandName;
  const isClickable = onClick && (showButtons || isPdp || !isGold);

  const completeText = useMemo(() => {
    if (!isPdp) {
      return 'Complete';
    }
    return `Complete ${isGold ? 'bundle' : 'deal'}`;
  }, [isPdp, isGold]);

  // In bag/checkout, hide specials that are already unlocked or product-level (nothing to act on)
  if ((isCheckout || isBag) && (isUnlocked || isProduct)) return null;

  return (
    <Card
      flat
      border="transparent-black-ten"
      className={styles['border__radius__lg']}
      width="100%"
      {...(isClickable && {
        onClick,
        ariaLabel: 'view special',
      })}
    >
      <Card.Content>
        <Flex gap={16} alignItems="center">
          {isGold && (
            <PartnerLogo
              name={brandName || ''}
              image={brandImage}
              variant="brand"
              size="xs"
              data-testid="partner-logo"
            />
          )}

          <Box overflow="hidden">
            {(isBag || showSignIn) && (
              <Typography color="text-light">
                {showBrandName && brandName}
                {showBrandName && showSignIn && (
                  <Typography mx={4} as="span" color="grays-mid">
                    &#8226;
                  </Typography>
                )}
                {showSignIn && 'Sign in for eligibility'}
              </Typography>
            )}
            {!isGold && specialTypeLabel && (
              <Typography color="info">{specialTypeLabel}</Typography>
            )}

            <Typography
              variant="body-semibold"
              maxLines={showBrandName || showSignIn ? 1 : 2}
            >
              {specialTitle}
            </Typography>

            {applicableWeights && applicableWeights.length > 0 && (
              <Flex gap={4} mt={8} flexWrap="wrap">
                {applicableWeights.map((weight) => (
                  <Tag
                    key={weight}
                    background="grays-ultralight"
                    color="grays-mid"
                    label={weight}
                    size="sm"
                  />
                ))}
              </Flex>
            )}
          </Box>

          <Flex grow>
            {showButtons && (
              <>
                <Button
                  className={styles['desktop-display']}
                  ml="auto"
                  label={completeText}
                  variant="tertiary"
                />
                <Button.Icon
                  className={styles['mobile-display']}
                  ml="auto"
                  variant="tertiary"
                  icon={<ChevronRightIcon size="sm" />}
                />
              </>
            )}

            {showCheck && (
              <Flex ml="auto">
                <CheckIcon className={styles['check-icon']} />
                <Typography className={styles['desktop-display']} ml={8}>
                  {/* TODO: Remove non-gold "Qualified" label once cart redesign ships. */}
                  {isGold ? 'Applied' : 'Qualified'}
                </Typography>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Card.Content>
    </Card>
  );
};
